import React, { useState } from "react";
import { usd } from "../../lib/api";
import { CheckCircle2, Clock, Copy, Check, Gift } from "lucide-react";

const PILL = {
  confirmed: ["bg-[#008057] text-white", "Confirmed"],
  pending_payment: ["bg-amber-100 text-amber-800", "Pending Payment"],
  cancelled: ["bg-red-100 text-red-700", "Cancelled"],
};
const JERSEY_SIZES = ["6-7Y", "8-9Y", "10-11Y", "12-13Y", "14-15Y", "Adult S", "Adult M", "Adult L", "Adult XL"];

function StatusPill({ status }) {
  const [cls, label] = PILL[status] || ["bg-slate-100 text-slate-600", status];
  return <span data-testid="confirmation-status" className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${cls}`}>{label}</span>;
}

export function ReceiptHeader({ o }) {
  const [copied, setCopied] = useState(false);
  const confirmed = o.registration_status === "confirmed";
  const copy = () => { navigator.clipboard.writeText(o.reference); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className={`px-6 py-6 ${confirmed ? "bg-[#008057]" : "bg-[#03281B]"} text-white`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#F3C242] font-bold">Registration Reference</div>
          <div className="font-display text-3xl font-extrabold flex items-center gap-3">{o.reference}
            <button data-testid="copy-reference" onClick={copy} className="text-white/70 hover:text-white">{copied ? <Check size={18} /> : <Copy size={18} />}</button>
          </div>
        </div>
        {confirmed ? <CheckCircle2 size={40} /> : <Clock size={40} />}
      </div>
      <div className="mt-3"><StatusPill status={o.registration_status} /></div>
    </div>
  );
}

export function BankTransferNotice({ o, bank }) {
  const rows = [["Bank", bank.bank], ["Account holder", bank.account_holder], ["Account #", bank.account_number, true], ["ACH routing", bank.routing_ach, true], ["Wire routing", bank.routing_wire, true]];
  return (
    <div data-testid="bank-transfer-notice" className="rounded-xl bg-amber-50 border border-amber-200 p-4">
      <div className="font-bold text-amber-800 mb-2">Complete your bank transfer</div>
      <p className="text-sm text-amber-700 mb-3">Your registration is <strong>Pending Payment</strong>. Transfer {usd(o.total)} and include reference <strong>{o.reference}</strong>. We'll confirm once funds are received.</p>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
        {rows.map(([l, v, mono]) => <div key={l}><span className="text-slate-500">{l}:</span> <strong className={mono ? "font-mono" : ""}>{v}</strong></div>)}
      </div>
    </div>
  );
}

export function JerseyGiftCard({ gift, size, setSize, onSave }) {
  return (
    <div data-testid="jersey-gift-section" className="rounded-xl bg-amber-50 border-2 border-[#F3C242] p-4">
      <div className="flex items-center gap-2 font-bold text-[#03281B] mb-1"><Gift size={18} className="text-[#B8860B]" /> Cristiano Ronaldo Jersey Awarded!</div>
      <p className="text-sm text-slate-600 mb-3">Congratulations — {gift.participant_name} qualifies for the retro replica jersey. Please confirm the size below.</p>
      <div className="flex gap-2">
        <select data-testid="jersey-size-select" className="rounded-xl border-2 border-[#E2EBF0] px-3 py-2 text-sm flex-1" value={size} onChange={(e) => setSize(e.target.value)}>
          <option value="">Select jersey size</option>{JERSEY_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button data-testid="save-jersey-size" onClick={onSave} disabled={!size} className="rounded-xl bg-[#008057] text-white px-4 py-2 font-bold text-sm disabled:opacity-50">Save</button>
      </div>
    </div>
  );
}

export function OrderSummaryList({ o }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-widest text-[#008057] mb-2">Order Summary</div>
      <div className="text-sm text-slate-600 mb-2">{o.category?.name} &middot; {o.payment_method === "stripe" ? "Credit Card" : "Bank Transfer"}</div>
      {o.participants.map((p) => (
        <div key={`${p.first_name}-${p.last_name}-${p.subtotal}`} className="border-t border-slate-100 py-2">
          <div className="font-semibold text-[#03281B]">{p.first_name} {p.last_name}</div>
          {(p.items || []).map((it) => <div key={it.label} className="flex justify-between text-sm text-slate-600"><span>{it.label}</span><span className="font-mono">{usd(it.amount)}</span></div>)}
        </div>
      ))}
      <div className="flex justify-between font-display font-extrabold text-xl text-[#03281B] border-t-2 border-[#008057] pt-2 mt-2"><span>{o.registration_status === "confirmed" ? "Amount Paid" : "Total Due"}</span><span data-testid="confirmation-total">{usd(o.total)}</span></div>
    </div>
  );
}
